/* What the extraction tables show while a task runs.
 *
 * They used to show a number this file no longer produces. A timer started when a row was first
 * seen running, ramped to 95% over a couple of minutes and then sat there — and it was drawn over
 * `progress_percentage` with a `Math.max`, so a task the backend honestly reported at 30% still
 * read 95%. Every slow task looked exactly like every dead one, and "stuck at 95%" became the way
 * testers described both.
 *
 * What replaces it: the backend's number when the backend has one, no number when it doesn't, and
 * how long the task has been running either way — the one fact that tells a slow task from a dead
 * one. */

export const TASK_STATUS = {
  COMPLETED: 'completed',
  IN_PROGRESS: 'in progress',
  /* Accepted by the server and handed straight back: the work is the server's, it has simply not
     reached this one yet. Read as anything other than running, a queue of sheets looks like a row
     of sheets nobody asked for. */
  QUEUED: 'queued',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  NOT_STARTED: 'not started'
};

/* How long a task may run before the row says so. Extraction of one sheet is minutes, not tens of
   them, so this is well clear of a healthy run and still short enough to catch a hung one inside a
   test session. It changes nothing about the task — it only stops the row from looking calm. */
export const SLOW_AFTER_MS = 10 * 60 * 1000;

/* The API sends naive timestamps — "2026-08-21T17:44:03.482308", no zone, no Z — and they are UTC;
   that is what the rest of the app assumes when it measures age (`dayjs.utc(created_at)` in the
   comment panels). Date.parse would read the same string as local time and put the start of a task
   an offset away from where it belongs, so stamp the zone on before parsing. */
export const parseApiTimestamp = value => {
  if (!value || typeof value !== 'string') return null;
  const withZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`;
  const parsed = Date.parse(withZone);
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizeStatus = status =>
  String(status ?? '')
    .trim()
    .toLowerCase();

const clampPercentage = value => Math.min(100, Math.max(0, Math.round(value)));

/* When the task says it started. The server timestamps are preferred — they survive a reload and
   they are the same for everyone looking at the row — but they are only used when they produce an
   age that could be true. A clock skew between the API host and this machine would otherwise be
   read as a stalled task, so anything in the future falls back to when this browser first saw the
   row running. */
const resolveStartedAt = ({ task, observedStartMs, nowMs }) => {
  const serverStart = parseApiTimestamp(task?.created_at);
  if (serverStart != null && serverStart <= nowMs) return serverStart;
  if (observedStartMs != null && observedStartMs <= nowMs) return observedStartMs;
  return null;
};

/**
 * @param {object} args
 * @param {object|null|undefined} args.task            latest_automation_task / latest_task
 * @param {number|null} [args.observedStartMs]         when this browser first saw the row running
 * @param {number} [args.nowMs]
 * @param {number} [args.slowAfterMs]
 * @returns {{
 *   status: string|null, isRunning: boolean, percentage: number|null,
 *   isIndeterminate: boolean, elapsedMs: number|null, isSlow: boolean
 * }}
 */
export function getTaskProgress({
  task,
  observedStartMs = null,
  nowMs = Date.now(),
  slowAfterMs = SLOW_AFTER_MS
} = {}) {
  const status = normalizeStatus(task?.status);
  const idle = {
    status: task?.status ?? null,
    isRunning: false,
    percentage: null,
    isIndeterminate: false,
    elapsedMs: null,
    isSlow: false
  };

  if (status === TASK_STATUS.COMPLETED) return { ...idle, percentage: 100 };
  if (
    status === TASK_STATUS.FAILED ||
    status === TASK_STATUS.CANCELLED ||
    status === TASK_STATUS.NOT_STARTED
  ) {
    return { ...idle, percentage: 0 };
  }
  if (status !== TASK_STATUS.IN_PROGRESS && status !== TASK_STATUS.QUEUED) return idle;

  const reported = task?.progress_percentage;
  /* A backend that reports 0 has told us as little as one that reports nothing, and a bar pinned at
     zero reads as broken. Both cases get no number and a bar that only says "moving". */
  const percentage =
    typeof reported === 'number' && Number.isFinite(reported) && reported > 0
      ? clampPercentage(reported)
      : null;

  const startedAt = resolveStartedAt({ task, observedStartMs, nowMs });
  const elapsedMs = startedAt == null ? null : Math.max(0, nowMs - startedAt);

  return {
    status: task?.status ?? null,
    isRunning: true,
    /* Never above 99 while it runs: 100 is what "Completed" means, and a row that reads 100% beside
       the word "In progress" is the same lie in a different place. */
    percentage: percentage == null ? null : Math.min(99, percentage),
    isIndeterminate: percentage == null,
    elapsedMs,
    isSlow: elapsedMs != null && elapsedMs >= slowAfterMs
  };
}

/** "38s", "4 min", "1 h 12 min" — short enough for a table cell. */
export function formatElapsed(ms) {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return null;
  const totalMinutes = Math.floor(ms / 60000);
  if (totalMinutes < 1) return `${Math.floor(ms / 1000)}s`;
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
}

export const isTaskRunning = task => {
  const status = normalizeStatus(task?.status);
  return status === TASK_STATUS.IN_PROGRESS || status === TASK_STATUS.QUEUED;
};
